import { ALL_VERTICALS, GLOBAL_COUNTRY } from "@app/shared"
import { Flag } from "@app/ui"
import { useQuery } from "@tanstack/react-query"
import { type CSSProperties, useEffect, useId, useRef, useState } from "react"
import { type Country, countriesQueryKeys, listCountries } from "../api/countries"
import { type Vertical, listVerticals, registriesQueryKeys } from "../api/registries"
import { useScope } from "../scope/scope-context"

/**
 * App-wide scope control: a compact "Scope" pill in the topbar showing the
 * current country × vertical, opening a two-column popover (Country |
 * Vertical). Wired to {@link useScope} (Task 7) + the live countries/verticals
 * registries. Country value = country `code`; vertical value = vertical `id`
 * — exactly what the engine filters on. Selecting drives `useScope`; screens
 * read it in Tasks 9–10.
 *
 * Ported from `design-system/dashboard-filters` ScopeSelector (structure/UX),
 * reconciled to helm conventions: `@app/ui` `Flag` for country glyphs, inline
 * styles + `@app/ui` tokens (helm has no CSS files), graceful empty fallback
 * on query error.
 */

const GOLD = "#C39A2C"
const EVERGREEN = "var(--gm-color-accent-bg)"
const CONSOLIDATED_SWATCH = `linear-gradient(135deg,${GOLD} 50%,#356B50 50%)`

/** Pick a swatch color from a vertical's name (cashew = gold, soybean = evergreen). */
function verticalSwatch(name: string): string {
	const n = name.toLowerCase()
	if (n.includes("cashew")) return GOLD
	if (n.includes("soy")) return EVERGREEN
	return "var(--gm-color-brand-9)"
}

function Swatch({ background }: { background: string }) {
	return (
		<span
			aria-hidden
			style={{
				display: "inline-block",
				width: 12,
				height: 12,
				borderRadius: 3,
				background,
				flex: "0 0 auto",
			}}
		/>
	)
}

/** Small globe glyph for the "Global (all countries)" option / pill. */
function Globe() {
	return (
		<svg
			aria-hidden="true"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			style={{ flex: "0 0 auto", opacity: 0.8 }}
		>
			<circle cx="12" cy="12" r="9" />
			<path d="M3 12h18M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
		</svg>
	)
}

function Chevron({ open }: { open: boolean }) {
	return (
		<svg
			aria-hidden="true"
			width="12"
			height="12"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			style={{
				flex: "0 0 auto",
				transform: open ? "rotate(180deg)" : "none",
				transition: "transform 120ms ease",
			}}
		>
			<path d="m6 9 6 6 6-6" />
		</svg>
	)
}

function CloseGlyph() {
	return (
		<svg
			aria-hidden="true"
			width="12"
			height="12"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M18 6 6 18M6 6l12 12" />
		</svg>
	)
}

function CheckGlyph() {
	return (
		<svg
			aria-hidden="true"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			style={{ flex: "0 0 auto" }}
		>
			<path d="M20 6 9 17l-5-5" />
		</svg>
	)
}

const optionBaseStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 8,
	width: "100%",
	padding: "6px 8px",
	border: "none",
	borderRadius: "var(--gm-radius-sm)",
	background: "transparent",
	color: "var(--gm-color-fg-default)",
	font: "inherit",
	fontSize: 13,
	textAlign: "left",
	cursor: "pointer",
}

function selectedStyle(selected: boolean): CSSProperties {
	return selected
		? {
				...optionBaseStyle,
				background: "var(--gm-color-accent-bg)",
				color: "var(--gm-color-accent-fg)",
			}
		: optionBaseStyle
}

const colHeadStyle: CSSProperties = {
	fontSize: 10,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase",
	color: "var(--gm-color-fg-subtle)",
	padding: "2px 8px 6px",
}

const grpHeadStyle: CSSProperties = {
	fontSize: 10,
	fontWeight: 600,
	letterSpacing: "0.04em",
	textTransform: "uppercase",
	color: "var(--gm-color-fg-muted)",
	padding: "8px 8px 2px",
}

export function ScopeSelector() {
	const { country, vertical, setCountry, setVertical, clear, isGlobal } = useScope()
	const [open, setOpen] = useState(false)
	const ref = useRef<HTMLDivElement>(null)
	const menuId = useId()

	const countriesQuery = useQuery({
		queryKey: countriesQueryKeys.all,
		queryFn: () => listCountries().catch(() => [] as Country[]),
	})
	const verticalsQuery = useQuery({
		queryKey: registriesQueryKeys.verticals(),
		queryFn: () => listVerticals().catch(() => [] as Vertical[]),
	})

	const countries = countriesQuery.data ?? []
	const verticals = verticalsQuery.data ?? []
	const operating = countries.filter((c) => c.role === "operating")
	const sourcing = countries.filter((c) => c.role === "sourcing")

	// Close on outside-click + Escape while open.
	useEffect(() => {
		if (!open) return
		function onDown(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setOpen(false)
		}
		document.addEventListener("mousedown", onDown)
		document.addEventListener("keydown", onKey)
		return () => {
			document.removeEventListener("mousedown", onDown)
			document.removeEventListener("keydown", onKey)
		}
	}, [open])

	// Resolve display names from the loaded lists; fall back to the raw
	// code/id while loading so the pill always renders.
	const selectedCountry = countries.find((c) => c.code === country)
	const selectedVertical = verticals.find((v) => v.id === vertical)
	const countryLabel = country === GLOBAL_COUNTRY ? "Global" : (selectedCountry?.name ?? country)
	const verticalLabel =
		vertical === ALL_VERTICALS ? "Consolidated" : (selectedVertical?.name ?? vertical)

	const countryOption = (opt: { code: string; name: string }, glyph: React.ReactNode) => {
		const sel = opt.code === country
		return (
			<button
				key={opt.code}
				type="button"
				// biome-ignore lint/a11y/useSemanticElements: a <button role="option"> is the pointer affordance for a single-select scope column; there is no native semantic element for a selectable option and aria-selected drives the visual checkmark.
				role="option"
				aria-selected={sel}
				style={selectedStyle(sel)}
				onClick={() => {
					setCountry(opt.code)
				}}
			>
				{glyph}
				<span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
					{opt.name}
				</span>
				{sel ? <CheckGlyph /> : null}
			</button>
		)
	}

	const verticalOption = (opt: { id: string; name: string }, swatch: string) => {
		const sel = opt.id === vertical
		return (
			<button
				key={opt.id}
				type="button"
				// biome-ignore lint/a11y/useSemanticElements: a <button role="option"> is the pointer affordance for a single-select scope column; there is no native semantic element for a selectable option and aria-selected drives the visual checkmark.
				role="option"
				aria-selected={sel}
				style={selectedStyle(sel)}
				onClick={() => {
					setVertical(opt.id)
				}}
			>
				<Swatch background={swatch} />
				<span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
					{opt.name}
				</span>
				{sel ? <CheckGlyph /> : null}
			</button>
		)
	}

	return (
		<div ref={ref} style={{ position: "relative", display: "inline-block" }}>
			<button
				type="button"
				aria-label="Scope filter"
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-controls={open ? menuId : undefined}
				onClick={() => setOpen((o) => !o)}
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: 8,
					height: 30,
					padding: "0 8px 0 10px",
					borderRadius: "var(--gm-radius-md)",
					border: `1px solid ${isGlobal ? "var(--gm-color-border-subtle)" : "var(--gm-color-accent-border)"}`,
					// Active = a SUBTLE accent tint (not the solid primary-button fill):
					// every foreground token here is dark (built for a light surface), so a
					// full accent-bg block both reads heavy and drops the text contrast.
					background: isGlobal
						? "var(--gm-color-bg-base)"
						: "color-mix(in srgb, var(--gm-color-accent-bg) 14%, var(--gm-color-bg-base))",
					color: "var(--gm-color-fg-default)",
					font: "inherit",
					fontSize: 13,
					cursor: "pointer",
				}}
			>
				<span
					style={{
						fontSize: 10,
						fontWeight: 700,
						letterSpacing: "0.06em",
						textTransform: "uppercase",
						color: "var(--gm-color-fg-subtle)",
					}}
				>
					Scope
				</span>
				<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
					{country === GLOBAL_COUNTRY ? (
						<Globe />
					) : (
						<Flag code={selectedCountry?.code ?? country} title={countryLabel} />
					)}
					<span>{countryLabel}</span>
					<span aria-hidden style={{ color: "var(--gm-color-fg-subtle)" }}>
						·
					</span>
					<Swatch
						background={
							vertical === ALL_VERTICALS ? CONSOLIDATED_SWATCH : verticalSwatch(verticalLabel)
						}
					/>
					<span>{verticalLabel}</span>
				</span>
				{!isGlobal ? (
					<span
						// biome-ignore lint/a11y/useSemanticElements: this clear affordance sits inside the trigger <button>; nesting a real <button> is invalid HTML, so a span[role=button] with keyboard handlers is the correct ARIA pattern here.
						role="button"
						tabIndex={0}
						aria-label="Clear scope"
						onClick={(e) => {
							e.stopPropagation()
							clear()
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault()
								e.stopPropagation()
								clear()
							}
						}}
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: 18,
							height: 18,
							borderRadius: "var(--gm-radius-sm)",
							color: "var(--gm-color-fg-muted)",
							cursor: "pointer",
						}}
					>
						<CloseGlyph />
					</span>
				) : null}
				<Chevron open={open} />
			</button>

			{open ? (
				<div
					id={menuId}
					// biome-ignore lint/a11y/useSemanticElements: role="dialog" marks the scope popover as a labelled dialog region; there is no native HTML element for an in-page (non-modal) popover dialog.
					role="dialog"
					aria-label="Scope filters"
					style={{
						position: "absolute",
						top: "calc(100% + 6px)",
						right: 0,
						zIndex: 50,
						minWidth: 360,
						background: "var(--gm-color-bg-raised)",
						border: "1px solid var(--gm-color-border-subtle)",
						borderRadius: "var(--gm-radius-lg)",
						boxShadow: "var(--gm-shadow-3)",
						overflow: "hidden",
					}}
				>
					<div style={{ display: "flex" }}>
						<div
							style={{
								flex: 1,
								padding: 8,
								borderRight: "1px solid var(--gm-color-border-subtle)",
								maxHeight: 360,
								overflowY: "auto",
							}}
						>
							<div style={colHeadStyle}>Country</div>
							{countryOption({ code: GLOBAL_COUNTRY, name: "Global (all countries)" }, <Globe />)}
							{operating.length > 0 ? (
								<div>
									<div style={grpHeadStyle}>Operating</div>
									{operating.map((c) => countryOption(c, <Flag code={c.code} title={c.name} />))}
								</div>
							) : null}
							{sourcing.length > 0 ? (
								<div>
									<div style={grpHeadStyle}>Sourcing</div>
									{sourcing.map((c) => countryOption(c, <Flag code={c.code} title={c.name} />))}
								</div>
							) : null}
						</div>
						<div style={{ flex: 1, padding: 8, maxHeight: 360, overflowY: "auto" }}>
							<div style={colHeadStyle}>Vertical</div>
							{verticalOption(
								{ id: ALL_VERTICALS, name: "Consolidated (all verticals)" },
								CONSOLIDATED_SWATCH,
							)}
							{verticals.map((v) => verticalOption(v, verticalSwatch(v.name)))}
						</div>
					</div>
					{!isGlobal ? (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 8,
								padding: "8px 10px",
								borderTop: "1px solid var(--gm-color-border-subtle)",
								background: "var(--gm-color-bg-subtle)",
							}}
						>
							<span style={{ fontSize: 12, color: "var(--gm-color-fg-muted)" }}>
								Applies to every screen
							</span>
							<button
								type="button"
								onClick={() => clear()}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 6,
									padding: "4px 8px",
									border: "1px solid var(--gm-color-border-default)",
									borderRadius: "var(--gm-radius-sm)",
									background: "var(--gm-color-bg-base)",
									color: "var(--gm-color-fg-default)",
									font: "inherit",
									fontSize: 12,
									cursor: "pointer",
								}}
							>
								Clear · back to Global
							</button>
						</div>
					) : null}
				</div>
			) : null}
		</div>
	)
}
